import {
  MODULES,
  NO_VALUE_KEY,
  addDays,
  formatPeso,
  spendAmount,
  type DateRange,
  type LedgerKind,
  type LocalDate,
  type PeriodChange,
  type RangePreset,
  type SpendDimension,
  type TimeGrain,
  type WeekStart,
} from '@tahi/core';
import { copy } from './copy';

export { spokenPeso } from '../../ui/spokenPeso';

/** Used until user_settings loads (and when the row doesn't exist): weeks start on Monday. */
export const DEFAULT_WEEK_START: WeekStart = 1;

export const WEEKDAY_SHORT = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'] as const;
const WEEKDAY_PLURAL = [
  'Sundays',
  'Mondays',
  'Tuesdays',
  'Wednesdays',
  'Thursdays',
  'Fridays',
  'Saturdays',
] as const;

function utc(d: LocalDate): Date {
  return new Date(
    Date.UTC(Number(d.slice(0, 4)), Number(d.slice(5, 7)) - 1, Number(d.slice(8, 10) || '1')),
  );
}

/** LocalDate is a calendar day, so format it in UTC to keep it from shifting. */
function fmt(d: LocalDate, o: Intl.DateTimeFormatOptions): string {
  return new Intl.DateTimeFormat('en-US', { timeZone: 'UTC', ...o }).format(utc(d));
}

const clean = (s: string) => s.replace(/ /g, ' ');

export function dayHeading(d: LocalDate, today: LocalDate): string {
  if (d === today) return copy.today;
  if (d === addDays(today, -1)) return 'Yesterday';
  const sameYear = d.slice(0, 4) === today.slice(0, 4);
  return fmt(d, {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    ...(sameYear ? {} : { year: 'numeric' }),
  });
}

export function shortDay(d: LocalDate, withYear = false): string {
  return fmt(d, { month: 'short', day: 'numeric', ...(withYear ? { year: 'numeric' } : {}) });
}

export function shortRange(r: DateRange): string {
  const crossYear = r.start.slice(0, 4) !== r.end.slice(0, 4);
  return `${shortDay(r.start, crossYear)} – ${shortDay(r.end, crossYear)}`;
}

export function monthShort(month: string): string {
  return fmt(`${month}-01`, { month: 'short' });
}

export function monthLong(month: string): string {
  return fmt(`${month}-01`, { month: 'long', year: 'numeric' });
}

export function rangeLabel(preset: RangePreset | 'custom', r: DateRange): string {
  switch (preset) {
    case 'week':
      return shortRange(r);
    case 'month':
      return monthLong(r.start.slice(0, 7));
    case 'quarter':
      return `Q${Math.floor((Number(r.start.slice(5, 7)) - 1) / 3) + 1} ${r.start.slice(0, 4)}`;
    case 'year':
      return r.start.slice(0, 4);
    case 'custom':
      return shortRange(r);
  }
}

export function timeLabel(grain: TimeGrain, key: string, rangeDays: number): string {
  if (grain === 'day')
    return rangeDays <= 7 ? fmt(key, { weekday: 'short' }) : String(Number(key.slice(8, 10)));
  if (grain === 'week') return shortDay(key);
  return monthShort(key);
}

export function sourceLabel(sourceModule: string): string {
  return (
    (MODULES as Record<string, { name: string } | undefined>)[sourceModule]?.name ?? sourceModule
  );
}

export function methodLabel(method: string | null): string {
  if (method === null || method === NO_VALUE_KEY) return 'Not set';
  if (method === 'cash') return 'Cash';
  if (method === 'account') return 'Account';
  if (method.startsWith('card:')) return 'Card';
  return method;
}

export function categoryName(
  id: string | null,
  byId: ReadonlyMap<string, { name: string }>,
): string {
  if (id === null || id === NO_VALUE_KEY) return copy.noCategory;
  // A deleted category reads as no category (its entries keep the id until it's restored).
  return byId.get(id)?.name ?? copy.noCategory;
}

/** Color by the category's place in the user's order, so a category keeps its color on every screen. */
export function categoryColor(id: string, orderedIds: readonly string[]): string {
  const i = orderedIds.indexOf(id);
  if (id === NO_VALUE_KEY || i < 0) return 'var(--chart-none)';
  return `var(--chart-${(i % 6) + 1})`;
}

export function bucketLabel(
  dim: SpendDimension,
  key: string,
  ctx: { categoryName: (id: string) => string },
): string {
  switch (dim) {
    case 'category':
      return ctx.categoryName(key);
    case 'day':
      return fmt(key, { weekday: 'short', month: 'short', day: 'numeric' });
    case 'week':
      return `Week of ${shortDay(key)}`;
    case 'month':
      return monthLong(key);
    case 'weekday':
      return WEEKDAY_PLURAL[Number(key)] ?? key;
    case 'source':
      return sourceLabel(key);
    case 'method':
      return methodLabel(key);
    case 'note':
      return `“${key}”`;
  }
}

const PREVIOUS: Record<RangePreset | 'custom', string> = {
  week: 'last week',
  month: 'last month',
  quarter: 'last quarter',
  year: 'last year',
  custom: 'the period before',
};

export function changeText(c: PeriodChange, preset: RangePreset | 'custom'): string {
  const prev = PREVIOUS[preset];
  if (c.previousMinor === 0 && c.currentMinor === 0) return '';
  if (c.previousMinor === 0) {
    return preset === 'custom'
      ? 'Nothing in the period before to compare with.'
      : `Nothing ${prev} to compare with.`;
  }
  if (c.changeMinor === 0) return `Same as ${prev}.`;
  const more = c.changeMinor > 0;
  const pct = c.changePct === null ? '' : ` (${more ? '+' : '−'}${Math.abs(c.changePct)}%)`;
  return `${formatPeso(Math.abs(c.changeMinor))} ${more ? 'more' : 'less'} than ${prev}${pct}.`;
}

export function formatWhen(at: string, tz: string): string {
  return clean(
    new Intl.DateTimeFormat('en-US', {
      timeZone: tz,
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    }).format(new Date(at)),
  );
}

export function entryTime(at: string, tz: string): string {
  return clean(
    new Intl.DateTimeFormat('en-US', { timeZone: tz, hour: 'numeric', minute: '2-digit' }).format(
      new Date(at),
    ),
  );
}

interface DayGroupable {
  id: string;
  date: LocalDate;
  amountMinor: number;
  kind: LedgerKind;
  deletedAt: string | null;
}

/** Entries must already be newest first. */
export function groupByDay<T extends DayGroupable>(
  entries: readonly T[],
): { date: LocalDate; totalMinor: number; entries: T[] }[] {
  const groups: { date: LocalDate; totalMinor: number; entries: T[] }[] = [];
  for (const e of entries) {
    let g = groups[groups.length - 1];
    if (!g || g.date !== e.date) {
      g = { date: e.date, totalMinor: 0, entries: [] };
      groups.push(g);
    }
    g.totalMinor += spendAmount(e);
    g.entries.push(e);
  }
  return groups;
}
